import { getSupabaseClient } from './supabase';
import type { PurchaseOrderLineRow } from './procurement';
import { VendorRentalInventoryError, rpcFailureOutcome } from './vendorRentalInventory';

/**
 * The purchase order record page (`/transfers/purchase-orders/:poId/*`, docs/warehouse.md): one
 * reader (`get_purchase_order_record`) and the keyed action RPCs. Every action takes a request key
 * (one per user action, reused when the same payload is retried), so a double submit or a retry
 * after a lost answer never applies twice.
 *
 * The reader's answer is normalised defensively: a missing or odd field becomes null, 0 or an empty
 * list instead of breaking the page.
 */

type Json = { [key: string]: unknown };

export type PurchaseOrderRef = { id: string; name: string | null };
export type PurchaseOrderVendorContact = { name: string | null; phone: string | null; email: string | null };
export type PurchaseOrderVendor = PurchaseOrderRef & { contact: PurchaseOrderVendorContact | null };
export type PurchaseOrderEventRef = {
  id: string;
  code: string | null;
  name: string | null;
  startDate: string | null;
  endDate: string | null;
};
export type PurchaseOrderLineEventRef = { id: string; code: string | null };
export type PurchaseOrderDocRef = { id: string; docNo: string | null };

export type PurchaseOrderSource = 'r2' | 'portal';
export type PurchaseOrderKind = 'goods' | 'labor';
export type PurchaseOrderLineTracking = 'serial' | 'quantity';

export type PurchaseOrderHeader = {
  id: string;
  docNo: string | null;
  status: string;
  source: PurchaseOrderSource;
  isR2: boolean;
  kind: PurchaseOrderKind;
  vendor: PurchaseOrderVendor | null;
  site: PurchaseOrderRef | null;
  orderedAt: string | null;
  /** Site-local `YYYY-MM-DD`; null when R2's expected date is just the order date. */
  expectedDay: string | null;
  r2SyncedAt: string | null;
  notes: string | null;
  orderedBy: PurchaseOrderRef | null;
  totalAmount: number | null;
  event: PurchaseOrderEventRef | null;
  serviceTicket: PurchaseOrderDocRef | null;
  daysLate: number | null;
  /** 'purchase' or 'cross_rental' (cross-rentals open as Subrentals). */
  poType: string;
};

export type PurchaseOrderRecordTotals = {
  lineCount: number;
  unitsOrdered: number;
  unitsReceived: number;
  receivedValue: number;
  openValue: number;
};

export type PurchaseOrderRecordLine = {
  id: string;
  item: { id: string; code: string | null; name: string | null } | null;
  description: string;
  qty: number;
  receivedQty: number;
  unitCost: number | null;
  extended: number | null;
  status: string;
  disposition: string;
  event: PurchaseOrderLineEventRef | null;
  serialsReceived: string[];
  /** Received or sold: quantity, use and status can no longer change. */
  locked: boolean;
  /** Serial lines receive one barcode per unit; quantity lines receive a count. */
  tracking: PurchaseOrderLineTracking;
};

export type PurchaseOrderRecordReceipt = {
  id: string;
  documentNo: string | null;
  receivedAt: string | null;
  receivedBy: PurchaseOrderRef | null;
  lineCount: number;
  units: number;
  status: string;
  filePath: string | null;
  /** A packing slip (scanned or uploaded) or a receive made by hand; null when unknown. */
  kind: 'slip' | 'manual' | null;
};

export type PurchaseOrderTimelineEntry = {
  id: string;
  at: string | null;
  kind: string;
  summary: string;
  actor: PurchaseOrderRef | null;
  details: Json | null;
};

export type PurchaseOrderActionGate = { allowed: boolean; reason: string | null };

export type PurchaseOrderActions = {
  receive: PurchaseOrderActionGate;
  editLines: PurchaseOrderActionGate;
  submit: PurchaseOrderActionGate;
  acknowledge: PurchaseOrderActionGate;
  close: PurchaseOrderActionGate;
  cancel: PurchaseOrderActionGate;
  setExpected: PurchaseOrderActionGate;
  scanSlip: PurchaseOrderActionGate;
};

export type PurchaseOrderRecord = {
  purchaseOrder: PurchaseOrderHeader;
  totals: PurchaseOrderRecordTotals;
  lines: PurchaseOrderRecordLine[];
  receipts: PurchaseOrderRecordReceipt[];
  timeline: PurchaseOrderTimelineEntry[];
  actions: PurchaseOrderActions;
};

export const purchaseOrderRecordKeys = {
  all: ['warehouse'] as const,
  record: (poId: string) => ['warehouse', 'purchase-order-record', poId] as const,
  history: (poId: string, filters: unknown) => ['warehouse', 'purchase-order-record', poId, 'history', filters] as const,
  openOrders: (term: string) => ['warehouse', 'purchase-order-picker', term] as const,
};

// ---------------------------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------------------------

function asObject(value: unknown): Json | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Json : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value);
  return text.length > 0 ? text : null;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = typeof value === 'number' ? value : Number(value ?? NaN);
  return Number.isFinite(number) ? number : fallback;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function asRef(value: unknown): PurchaseOrderRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, name: asText(object?.name) } : null;
}

function asDocRef(value: unknown): PurchaseOrderDocRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  return id ? { id, docNo: asText(object?.doc_no) } : null;
}

function asVendor(value: unknown): PurchaseOrderVendor | null {
  const ref = asRef(value);
  if (!ref) return null;
  const contact = asObject(asObject(value)?.contact);
  const normalizedContact = contact
    ? { name: asText(contact.name), phone: asText(contact.phone), email: asText(contact.email) }
    : null;
  const hasContact = Boolean(normalizedContact && (normalizedContact.name || normalizedContact.phone || normalizedContact.email));
  return { ...ref, contact: hasContact ? normalizedContact : null };
}

function asEvent(value: unknown): PurchaseOrderEventRef | null {
  const object = asObject(value);
  const id = asText(object?.id);
  if (!id) return null;
  return {
    id,
    code: asText(object?.code),
    name: asText(object?.name),
    startDate: asText(object?.start_date),
    endDate: asText(object?.end_date),
  };
}

function asGate(value: unknown): PurchaseOrderActionGate {
  const object = asObject(value);
  if (!object) return { allowed: value === true, reason: null };
  return { allowed: object.allowed === true, reason: asText(object.reason) };
}

/**
 * The same rule as the receive function (private.purchase_order_inventory_supply): a catalog item
 * whose type is `serial`, or a catalog item with no type that is not bulk, receives one barcode per
 * unit. The reader's own `tracking` (or `inventory_kind`) wins when it sends one.
 */
function lineTracking(line: Json, item: Json | null): PurchaseOrderLineTracking {
  const tracking = asText(line.tracking) ?? asText(line.inventory_kind) ?? asText(item?.tracking);
  if (tracking === 'serial') return 'serial';
  if (tracking) return 'quantity';
  if (!item || !asText(item.id)) return 'quantity';
  const role = asText(item.behavior_role);
  if (role) return role === 'serial' ? 'serial' : 'quantity';
  if (Object.prototype.hasOwnProperty.call(item, 'is_bulk')) return item.is_bulk === true ? 'quantity' : 'serial';
  return 'quantity';
}

function parseLine(value: unknown): PurchaseOrderRecordLine | null {
  const line = asObject(value);
  const id = asText(line?.id ?? line?.po_line_id);
  if (!line || !id) return null;
  const itemObject = asObject(line.item);
  const itemId = asText(itemObject?.id);
  const qty = asNumber(line.qty);
  const receivedQty = asNumber(line.received_qty);
  const unitCost = asNullableNumber(line.unit_cost);
  const eventObject = asObject(line.event);
  const eventId = asText(eventObject?.id);
  const serials = asArray(line.serials_received)
    .map((serial) => asText(asObject(serial)?.code ?? serial))
    .filter((serial): serial is string => Boolean(serial));
  return {
    id,
    item: itemId ? { id: itemId, code: asText(itemObject?.code), name: asText(itemObject?.name) } : null,
    description: asText(line.description) ?? '',
    qty,
    receivedQty,
    unitCost,
    extended: asNullableNumber(line.extended) ?? (unitCost == null ? null : Math.round(qty * unitCost * 100) / 100),
    status: asText(line.status) ?? 'pending',
    disposition: asText(line.disposition) ?? 'inventory',
    event: eventId ? { id: eventId, code: asText(eventObject?.code) } : null,
    serialsReceived: serials,
    locked: line.locked === true || receivedQty > 0 || asNumber(line.sold_qty) > 0,
    tracking: lineTracking(line, itemObject),
  };
}

/** The reader's `kind` ('slip' | 'manual'), else its `source_kind` ('scan_import' | 'manual'). */
function receiptKind(receipt: Json): 'slip' | 'manual' | null {
  const raw = asText(receipt.kind) ?? asText(receipt.source_kind);
  if (raw === 'manual') return 'manual';
  if (raw === 'slip' || raw === 'scan_import') return 'slip';
  return null;
}

function parseReceipt(value: unknown): PurchaseOrderRecordReceipt | null {
  const receipt = asObject(value);
  const id = asText(receipt?.id ?? receipt?.po_receipt_id);
  if (!receipt || !id) return null;
  return {
    id,
    documentNo: asText(receipt.document_no ?? receipt.document_number),
    receivedAt: asText(receipt.received_at),
    receivedBy: asRef(receipt.received_by),
    lineCount: asNumber(receipt.line_count),
    units: asNumber(receipt.units),
    status: asText(receipt.status) ?? 'draft',
    filePath: asText(receipt.file_path),
    kind: receiptKind(receipt),
  };
}

function parseTimelineEntry(value: unknown, index: number): PurchaseOrderTimelineEntry | null {
  const entry = asObject(value);
  if (!entry) return null;
  const summary = asText(entry.summary);
  if (!summary) return null;
  return {
    id: asText(entry.id) ?? `timeline-${index}`,
    at: asText(entry.at),
    kind: asText(entry.kind) ?? 'note',
    summary,
    actor: asRef(entry.actor),
    details: asObject(entry.details),
  };
}

function parseHeader(value: unknown, fallbackId: string): PurchaseOrderHeader {
  const po = asObject(value) ?? {};
  const source: PurchaseOrderSource = po.source === 'r2' || po.is_r2 === true ? 'r2' : 'portal';
  return {
    id: asText(po.id) ?? fallbackId,
    docNo: asText(po.doc_no),
    status: asText(po.status) ?? 'draft',
    source,
    isR2: source === 'r2',
    kind: po.kind === 'labor' ? 'labor' : 'goods',
    vendor: asVendor(po.vendor),
    site: asRef(po.site),
    orderedAt: asText(po.ordered_at),
    expectedDay: asText(po.expected_day),
    r2SyncedAt: asText(po.r2_synced_at),
    notes: asText(po.notes),
    orderedBy: asRef(po.ordered_by),
    totalAmount: asNullableNumber(po.total_amount),
    event: asEvent(po.event),
    serviceTicket: asDocRef(po.service_ticket),
    daysLate: asNullableNumber(po.days_late),
    poType: asText(po.po_type) ?? 'purchase',
  };
}

function parseTotals(value: unknown, lines: PurchaseOrderRecordLine[]): PurchaseOrderRecordTotals {
  const totals = asObject(value);
  const live = lines.filter((line) => line.status !== 'cancelled');
  const computed = {
    lineCount: lines.length,
    unitsOrdered: live.reduce((sum, line) => sum + line.qty, 0),
    unitsReceived: live.reduce((sum, line) => sum + line.receivedQty, 0),
    receivedValue: live.reduce((sum, line) => sum + line.receivedQty * (line.unitCost ?? 0), 0),
    openValue: live.reduce((sum, line) => sum + Math.max(0, line.qty - line.receivedQty) * (line.unitCost ?? 0), 0),
  };
  if (!totals) return computed;
  return {
    lineCount: asNumber(totals.line_count, computed.lineCount),
    unitsOrdered: asNumber(totals.units_ordered, computed.unitsOrdered),
    unitsReceived: asNumber(totals.units_received, computed.unitsReceived),
    receivedValue: asNumber(totals.received_value, computed.receivedValue),
    openValue: asNumber(totals.open_value, computed.openValue),
  };
}

export function parsePurchaseOrderRecord(data: unknown, poId: string): PurchaseOrderRecord {
  const object = asObject(data) ?? {};
  const lines = asArray(object.lines)
    .map(parseLine)
    .filter((line): line is PurchaseOrderRecordLine => line != null);
  const actions = asObject(object.actions) ?? {};
  return {
    purchaseOrder: parseHeader(object.purchase_order, poId),
    totals: parseTotals(object.totals, lines),
    lines,
    receipts: asArray(object.receipts)
      .map(parseReceipt)
      .filter((receipt): receipt is PurchaseOrderRecordReceipt => receipt != null),
    timeline: asArray(object.timeline)
      .map(parseTimelineEntry)
      .filter((entry): entry is PurchaseOrderTimelineEntry => entry != null),
    actions: {
      receive: asGate(actions.receive),
      editLines: asGate(actions.edit_lines),
      submit: asGate(actions.submit),
      acknowledge: asGate(actions.acknowledge),
      close: asGate(actions.close),
      cancel: asGate(actions.cancel),
      setExpected: asGate(actions.set_expected),
      scanSlip: asGate(actions.scan_slip),
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

/**
 * A refusal or failure with a sentence meant for the person. It deliberately carries no `code`,
 * so the shared error translators show its message instead of a generic fallback. `outcome` says
 * whether the server refused the call ('rejected', nothing changed) or the answer never came
 * ('unknown', it may have applied).
 */
export class PurchaseOrderActionError extends Error {
  readonly reason: string | null;
  readonly outcome: 'rejected' | 'unknown';

  constructor(message: string, reason: string | null = null, outcome: 'rejected' | 'unknown' = 'rejected') {
    super(message);
    this.name = 'PurchaseOrderActionError';
    this.reason = reason;
    this.outcome = outcome;
  }
}

/** Whether a failed Warehouse action was refused ('rejected') or may have applied ('unknown'). */
export function requestOutcome(error: unknown): 'rejected' | 'unknown' {
  if (error instanceof PurchaseOrderActionError || error instanceof VendorRentalInventoryError) return error.outcome;
  return rpcFailureOutcome(error);
}

const PERMISSION_MESSAGE =
  'Your account can view this purchase order but not change it. Ask an administrator for edit access to purchase orders.';
const CHANGED_MESSAGE = 'This purchase order changed since the page loaded. Refresh it, then try again.';
const NETWORK_MESSAGE = 'The app could not reach the service. Check your connection and try again.';
/** postgrest-js reports a failed fetch as `${error.name}: ${error.message}`. */
const WRAPPED_FETCH_ERROR = /^([A-Za-z]*Error):\s*(.+)$/s;
const NETWORK_TEXT = /failed to fetch|networkerror|load failed|network request failed/i;

const KNOWN_REASONS: { readonly [key: string]: string } = {
  purchase_order_receipt_permission_required: PERMISSION_MESSAGE,
  purchase_order_receive_quantity_required: 'Count at least one unit before receiving.',
  purchase_order_receive_exceeds_remaining_quantity:
    'That is more than the line still expects. Check the counts, then try again.',
  serialized_purchase_order_units_required: 'Scan or type one barcode for each serialized unit.',
  serialized_purchase_order_barcode_required: 'Each serialized unit needs a barcode.',
  serialized_purchase_order_unit_invalid: 'One of the scanned units could not be read. Scan it again.',
  bulk_purchase_order_asset_units_not_allowed: 'This line is counted by quantity. Count it instead of scanning serials.',
  noninventory_purchase_order_asset_units_not_allowed: 'This line is not tracked by serial. Count it instead.',
  cross_rental_receive_requires_vendor_rental_inventory: 'Cross-rental lines are received on the Subrental record.',
  purchase_order_location_required: 'This purchase order has no site. Set the site before receiving.',
  purchase_order_line_not_found: 'A line on this order no longer exists. Refresh the page.',
  receipt_already_applied: 'This packing slip was already applied. Applied slips can’t be applied twice.',
  po_line_not_found_or_wrong_po: 'A matched line is not on this purchase order. Check the matches, then try again.',
  warehouse_request_key_reused: 'This action was already sent with other details. Close the dialog and try again.',
};

const MACHINE_KEY = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;
const TECHNICAL_TEXT = /\b[a-z]+(?:_[a-z0-9]+)+\b|\b(?:PGRST\d+|SQLSTATE)\b|\b[0-9a-f]{8}-[0-9a-f-]{27,}\b|https?:\/\//i;
/** Postgres's own wording, never meant for the person. */
const DATABASE_TEXT = /^(?:permission denied|new row violates|null value in column|duplicate key value|insert or update on table|update or delete on table|value too long|invalid input syntax|(?:column|relation|function|schema) .+ does not exist|could not|canceling statement|deadlock detected)/i;

function readableSentence(text: string | null): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 400 || !/\s/.test(trimmed)) return null;
  if (TECHNICAL_TEXT.test(trimmed) || DATABASE_TEXT.test(trimmed)) return null;
  return trimmed;
}

function errorField(error: unknown, field: 'message' | 'hint' | 'details' | 'code'): string | null {
  if (field === 'message' && error instanceof Error) return error.message;
  const object = asObject(error);
  return object ? asText(object[field]) : null;
}

/**
 * What to tell the person about a failed reader or action call. Warehouse refusals put a
 * person-readable sentence in MESSAGE and their key in HINT; older functions raise the key as the
 * message. Known keys get their own sentence; anything else falls back to `fallback`.
 */
export function purchaseOrderErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof PurchaseOrderActionError) return error.message;
  const message = errorField(error, 'message');
  const hint = errorField(error, 'hint');
  const details = errorField(error, 'details');
  const code = errorField(error, 'code');
  const key = [hint, message].find((value): value is string => Boolean(value && MACHINE_KEY.test(value.trim())))?.trim() ?? null;
  if (key && KNOWN_REASONS[key]) return KNOWN_REASONS[key];
  const wrapped = message ? message.trim().match(WRAPPED_FETCH_ERROR) : null;
  if (wrapped) {
    // The app's own AppError text is already written for people; anything else is technical.
    if (NETWORK_TEXT.test(wrapped[2])) return NETWORK_MESSAGE;
    return wrapped[1] === 'AppError' ? wrapped[2].trim() : fallback;
  }
  if (message && NETWORK_TEXT.test(message)) return NETWORK_MESSAGE;
  const sentence = readableSentence(message);
  if (sentence) return sentence;
  if (code === '42501' || (key && key.includes('permission'))) return PERMISSION_MESSAGE;
  if (code === 'P0002' || (key && key.endsWith('not_found'))) {
    return 'This purchase order (or one of its lines) no longer exists. Refresh the page.';
  }
  if (code === '23505' && /barcode|asset/i.test(`${message ?? ''} ${details ?? ''}`)) {
    return 'One of these barcodes is already in inventory. Check it, then try again.';
  }
  if (code === '55000' || code === '40001') return CHANGED_MESSAGE;
  return readableSentence(details) ?? fallback;
}

function toActionError(error: unknown, fallback: string): PurchaseOrderActionError {
  const object = asObject(error);
  const reason = asText(object?.hint) ?? asText(object?.message) ?? null;
  return new PurchaseOrderActionError(purchaseOrderErrorMessage(error, fallback), reason, rpcFailureOutcome(error));
}

// ---------------------------------------------------------------------------------------------
// Request keys
// ---------------------------------------------------------------------------------------------

/** A v4 UUID for one user action; falls back to getRandomValues outside secure contexts. */
export function newPurchaseOrderRequestKey(): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') cryptoApi.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ---------------------------------------------------------------------------------------------
// Reader
// ---------------------------------------------------------------------------------------------

export async function fetchPurchaseOrderRecord(poId: string): Promise<PurchaseOrderRecord> {
  const { data, error } = await getSupabaseClient().rpc('get_purchase_order_record', { p_po_id: poId });
  if (error && error.code === '42501') {
    throw new PurchaseOrderActionError('Your account does not have access to this purchase order.', 'purchase_order_access');
  }
  if (error) throw toActionError(error, 'The purchase order could not be loaded. Try again.');
  if (data == null) throw new PurchaseOrderActionError('This purchase order was not found, or you do not have access to it.', 'purchase_order_not_found');
  return parsePurchaseOrderRecord(data, poId);
}

// ---------------------------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------------------------

export type PurchaseOrderStatusAction = 'submit' | 'acknowledge' | 'close' | 'cancel' | 'set_expected';

export type PurchaseOrderActionResult = { replayed: boolean; status: string | null };

function actionResult(data: unknown): PurchaseOrderActionResult {
  const object = asObject(data);
  return { replayed: object?.replayed === true, status: asText(object?.status) };
}

/** Submit, Mark acknowledged, Close, Cancel, or Change expected date (`expectedAt` for that one). */
export async function setPurchaseOrderStatus(params: {
  poId: string;
  requestKey: string;
  action: PurchaseOrderStatusAction;
  expectedAt?: string | null;
}): Promise<PurchaseOrderActionResult> {
  const { data, error } = await getSupabaseClient().rpc('purchase_order_set_status', {
    p_po_id: params.poId,
    p_request_key: params.requestKey,
    p_action: params.action,
    p_expected_at: params.action === 'set_expected' ? params.expectedAt ?? null : null,
  });
  if (error) throw toActionError(error, 'The purchase order was not changed. Try again.');
  return actionResult(data);
}

export type PurchaseOrderReceiveLine = {
  poLineId: string;
  qty: number;
  /** Serialized lines: one barcode per unit received. */
  assetUnits?: Array<{ barcode: string; serialNumber?: string | null }>;
};

/** Receives several lines in one transaction (wraps receive_purchase_order_inventory_line). */
export async function receivePurchaseOrderItems(params: {
  poId: string;
  requestKey: string;
  lines: PurchaseOrderReceiveLine[];
  note?: string | null;
}): Promise<PurchaseOrderActionResult> {
  const lines = params.lines
    .filter((line) => line.poLineId && line.qty > 0)
    .map((line) => ({
      po_line_id: line.poLineId,
      qty: line.qty,
      ...(line.assetUnits && line.assetUnits.length
        ? { asset_units: line.assetUnits.map((unit) => ({ barcode: unit.barcode, serial_number: unit.serialNumber ?? null })) }
        : {}),
    }));
  if (!lines.length) throw new PurchaseOrderActionError('Count at least one unit before receiving.', 'purchase_order_receive_quantity_required');
  const { data, error } = await getSupabaseClient().rpc('purchase_order_receive', {
    p_po_id: params.poId,
    p_request_key: params.requestKey,
    p_lines: lines,
    p_note: params.note?.trim() || null,
  });
  if (error) throw toActionError(error, 'Nothing was received. Your count is kept, so you can try again.');
  return actionResult(data);
}

/**
 * One line for purchase_order_save_lines: an existing line (`poLineId`) with only the fields that
 * change, or a new line. A line's status is never sent: the order's actions own it.
 */
export type PurchaseOrderLineSave = {
  poLineId?: string;
  itemId?: string | null;
  description?: string;
  qty?: number;
  unitCost?: number | null;
  disposition?: string;
};

export type PurchaseOrderLinesSaved = {
  replayed: boolean;
  totalAmount: number | null;
  insertedLineIds: string[];
};

function lineSavePayload(line: PurchaseOrderLineSave): Json {
  const payload: Json = {};
  if (line.poLineId) payload.po_line_id = line.poLineId;
  if (line.itemId !== undefined) payload.item_id = line.itemId;
  if (line.description !== undefined) payload.description = line.description;
  if (line.qty !== undefined) payload.qty = line.qty;
  if (line.unitCost !== undefined) payload.unit_cost = line.unitCost;
  if (line.disposition !== undefined) payload.disposition = line.disposition;
  return payload;
}

/**
 * Line edits and new lines through purchase_order_save_lines, so a Portal order's total is
 * recomputed and the order's own rules apply (received lines keep their item, quantity and use; a
 * closed or cancelled order's lines are refused). The fingerprint is what the request key covers.
 */
export function purchaseOrderLinesFingerprint(poId: string, lines: readonly PurchaseOrderLineSave[]): string {
  return JSON.stringify({ poId, lines: lines.map(lineSavePayload) });
}

export async function savePurchaseOrderLines(params: {
  poId: string;
  requestKey: string;
  lines: readonly PurchaseOrderLineSave[];
}): Promise<PurchaseOrderLinesSaved> {
  let response;
  try {
    response = await getSupabaseClient().rpc('purchase_order_save_lines', {
      p_po_id: params.poId,
      p_request_key: params.requestKey,
      p_lines: params.lines.map(lineSavePayload),
    });
  } catch (error) {
    throw toActionError(error, 'The line change was not saved. Try again.');
  }
  if (response.error) throw toActionError(response.error, 'The line change was not saved. Try again.');
  const object = asObject(response.data);
  return {
    replayed: object?.replayed === true,
    totalAmount: asNullableNumber(object?.total_amount),
    insertedLineIds: asArray(object?.inserted_line_ids).map((id) => asText(id)).filter((id): id is string => Boolean(id)),
  };
}

export type NewPurchaseOrderLineInput = {
  itemId?: string | null;
  description: string;
  qty: number;
  unitCost: number | null;
  disposition: 'inventory' | 'event_sale';
  eventId?: string | null;
};

export type NewPurchaseOrderInput = {
  locationId: string;
  vendorId: string | null;
  purchaseKind: PurchaseOrderKind;
  /** Timestamp of the expected delivery, or null. */
  expectedDeliveryAt: string | null;
  notes: string | null;
  eventId: string | null;
  lines: NewPurchaseOrderLineInput[];
};

export function newPurchaseOrderPayload(input: NewPurchaseOrderInput): Json {
  return {
    location_id: input.locationId,
    vendor_id: input.vendorId || null,
    purchase_kind: input.purchaseKind,
    expected_delivery_at: input.expectedDeliveryAt || null,
    notes: input.notes?.trim() || null,
    event_id: input.eventId || null,
    lines: input.lines.map((line) => ({
      ...(line.itemId ? { item_id: line.itemId } : {}),
      description: line.description.trim(),
      qty: line.qty,
      unit_cost: line.unitCost,
      disposition: line.disposition,
      event_id: line.eventId || null,
    })),
  };
}

/** Creates a Portal purchase order; the server assigns its P-number. */
export async function createPurchaseOrder(params: {
  requestKey: string;
  po: NewPurchaseOrderInput;
}): Promise<{ poId: string; docNo: string | null; replayed: boolean }> {
  const { data, error } = await getSupabaseClient().rpc('create_purchase_order', {
    p_request_key: params.requestKey,
    p_po: newPurchaseOrderPayload(params.po),
  });
  if (error) throw toActionError(error, 'The purchase order was not created. Try again.');
  const object = asObject(data);
  const poId = asText(object?.po_id ?? object?.id);
  if (!poId) throw new PurchaseOrderActionError('The purchase order was created, but its number did not come back. Refresh the Purchase orders list to find it.');
  return { poId, docNo: asText(object?.doc_no), replayed: object?.replayed === true };
}

/** "Report a problem" and other business notes on the record's history. */
export async function addPurchaseOrderEvent(params: {
  poId: string;
  requestKey: string;
  eventType: string;
  notes: string | null;
  details?: Json | null;
}): Promise<void> {
  const { error } = await getSupabaseClient().rpc('procurement_record_add_event', {
    p_kind: 'purchase_order',
    p_record_id: params.poId,
    p_request_key: params.requestKey,
    p_event_type: params.eventType,
    p_notes: params.notes?.trim() || null,
    p_details: params.details ?? {},
  });
  if (error) throw toActionError(error, 'The problem was not saved. Try again.');
}

// ---------------------------------------------------------------------------------------------
// Packing slips
// ---------------------------------------------------------------------------------------------

/** The record's lines in the shape the packing-slip scan route sends to the reader. */
export function purchaseOrderScanLines(
  poId: string,
  lines: readonly PurchaseOrderRecordLine[],
): Array<PurchaseOrderLineRow & { item_name: string | null; item_code: string | null }> {
  return lines.map((line) => ({
    po_line_id: line.id,
    po_id: poId,
    description: line.description,
    item_id: line.item?.id ?? null,
    qty: line.qty,
    unit_cost: line.unitCost,
    received_qty: line.receivedQty,
    status: line.status,
    disposition: line.disposition,
    item_name: line.item?.name ?? null,
    item_code: line.item?.code ?? null,
  }));
}

export type OpenPurchaseOrderOption = {
  id: string;
  docNo: string | null;
  status: string;
  isR2: boolean;
  vendorName: string | null;
  siteName: string | null;
  orderedAt: string | null;
  expectedAt: string | null;
  totalAmount: number | null;
};

/** Statuses a packing slip can still be received against. */
export const RECEIVABLE_PURCHASE_ORDER_STATUSES = ['submitted', 'acknowledged', 'partially_received'] as const;
const OPEN_ORDER_LIMIT = 25;
const OPEN_ORDER_SELECT =
  'po_id,doc_no,status,ordered_at,expected_delivery_at,total_amount,r2_po_iid,r2_po_id,vendors(name),locations(name)';

function cleanSearchTerm(term: string): string {
  return term.replace(/[%*(),.:\\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
}

function firstName(value: unknown): string | null {
  const object = Array.isArray(value) ? asObject(value[0]) : asObject(value);
  return asText(object?.name);
}

function isMissingColumn(error: { code?: string; message?: string } | null): boolean {
  return Boolean(error && (error.code === '42703' || /column .* does not exist/i.test(error.message ?? '')));
}

/**
 * Open goods purchase orders for the "Scan packing slip" picker: newest first, at most 25, searched
 * on the server by document number, R2 PO number or vendor name. Cross-rentals and labor orders are
 * left out (they have no packing slips).
 */
export async function searchOpenPurchaseOrders(term: string): Promise<OpenPurchaseOrderOption[]> {
  const supabase = getSupabaseClient();
  const cleaned = cleanSearchTerm(term);
  const clauses: string[] = [];
  if (cleaned) {
    const pattern = `%${cleaned.replace(/\s+/g, '%')}%`;
    clauses.push(`doc_no.ilike.${pattern}`, `r2_po_id.ilike.${pattern}`);
    const vendors = await supabase.from('vendors').select('vendor_id').ilike('name', pattern).limit(50);
    if (vendors.error) throw toActionError(vendors.error, 'Purchase orders could not be searched. Try again.');
    const vendorIds = (vendors.data ?? [])
      .map((row: { vendor_id?: string | null }) => row.vendor_id)
      .filter((id): id is string => Boolean(id));
    if (vendorIds.length) clauses.push(`vendor_id.in.(${vendorIds.join(',')})`);
  }
  const notLabor = 'or(r2_order_type.is.null,r2_order_type.neq.2)';
  const build = (withKind: boolean) => {
    const parts = [notLabor, ...(withKind ? ['or(purchase_kind.is.null,purchase_kind.neq.labor)'] : []), ...(clauses.length ? [`or(${clauses.join(',')})`] : [])];
    return supabase
      .from('purchase_orders')
      .select(OPEN_ORDER_SELECT)
      .eq('po_type', 'purchase')
      .in('status', [...RECEIVABLE_PURCHASE_ORDER_STATUSES])
      .or(`and(${parts.join(',')})`)
      .order('ordered_at', { ascending: false, nullsFirst: false })
      .limit(OPEN_ORDER_LIMIT);
  };
  let result = await build(true);
  // purchase_kind arrives with the Warehouse migrations; before them every Portal order is goods.
  if (isMissingColumn(result.error)) result = await build(false);
  if (result.error) throw toActionError(result.error, 'Purchase orders could not be searched. Try again.');
  return (result.data ?? []).map((row: Json) => ({
    id: String(row.po_id),
    docNo: asText(row.doc_no),
    status: asText(row.status) ?? 'submitted',
    isR2: row.r2_po_iid != null || Boolean(asText(row.r2_po_id)),
    vendorName: firstName(row.vendors),
    siteName: firstName(row.locations),
    orderedAt: asText(row.ordered_at),
    expectedAt: asText(row.expected_delivery_at),
    totalAmount: asNullableNumber(row.total_amount),
  }));
}
